import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  FiActivity,
  FiCalendar,
  FiClock,
  FiCompass,
  FiLayers,
  FiMonitor,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from 'react-icons/fi'
import { fetchStaffDashboard } from '../../store/slices/teacherSlice'
import './Overview.css'

const formatLongDate = (date) =>
  date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })

const formatShortTime = (date) =>
  date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })

const formatLectureWindow = (start, end) => {
  const now = new Date()
  const todayKey = now.toDateString()
  const lectureDay = start.toDateString()
  const dayLabel = todayKey === lectureDay ? 'Today' : start.toLocaleDateString('en-US', { weekday: 'short' })
  const durationMinutes = end ? Math.max(1, Math.round((end - start) / 60000)) : null
  const durationLabel = durationMinutes ? `${durationMinutes} min` : '—'
  return {
    slot: `${dayLabel} · ${formatShortTime(start)}`,
    duration: durationLabel,
  }
}

const statusBadge = (status) => {
  if (!status) return 'Scheduled'
  return status.replace(/_/g, ' ')
}

const Overview = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { dashboard, isLoading, error } = useSelector((state) => state.teacher)
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!dashboard && !isLoading) {
      dispatch(fetchStaffDashboard())
    }
  }, [dashboard, isLoading, dispatch])

  const greetingMessage = useMemo(() => {
    const hour = now.getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }, [now])

  const formattedDate = useMemo(() => formatLongDate(now), [now])
  const formattedTime = useMemo(() => formatShortTime(now), [now])

  const highlightMetrics = useMemo(() => {
    if (!dashboard?.stats) {
      return [
        {
          icon: FiUsers,
          label: 'Active students',
          value: '—',
          trend: 'Loading insights',
        },
        {
          icon: FiTrendingUp,
          label: 'Upcoming sessions',
          value: '—',
          trend: 'Syncing calendar',
        },
        {
          icon: FiClock,
          label: 'Recent sessions',
          value: '—',
          trend: 'Gathering activity',
        },
      ]
    }

    const { stats, upcomingLectures = [], recentLectures = [] } = dashboard
    const batchCount =
      stats?.assignedBatches ?? stats?.managedBatches ?? stats?.totalBatches ?? 0
    const nextLecture = upcomingLectures[0]
    const nextLectureTime = nextLecture
      ? formatLectureWindow(new Date(nextLecture.startTime), new Date(nextLecture.endTime)).slot
      : 'No sessions scheduled'

    return [
      {
        icon: FiUsers,
  label: 'Active students',
        value: stats?.totalStudents ?? 0,
  trend: `Across ${batchCount} batches`,
      },
      {
        icon: FiTrendingUp,
        label: 'Upcoming sessions',
        value: stats?.upcomingLectures ?? stats?.totalLectures ?? 0,
        trend: nextLectureTime,
      },
      {
        icon: FiClock,
        label: 'Recent sessions',
        value: stats?.recentLectures ?? stats?.totalLectures ?? 0,
        trend: 'Last 7 days delivered',
      },
    ]
  }, [dashboard])

  const quickActions = useMemo(
    () => [
      {
        icon: FiZap,
        label: 'Manage batches',
        caption: 'Assign teachers and students',
        to: '/batches',
      },
      {
        icon: FiLayers,
        label: 'Craft a module',
        caption: 'Shape the next experience',
        to: '/lectures/new',
      },
      {
        icon: FiCalendar,
        label: 'Intelligent schedule',
        caption: 'Auto-plan your upcoming sprint',
        to: '/lectures/schedule',
      },
    ],
    []
  )

  const scheduleEntries = useMemo(() => {
    if (!dashboard?.upcomingLectures?.length) return []

    return dashboard.upcomingLectures.map((lecture) => {
      const start = new Date(lecture.startTime)
      const end = lecture.endTime ? new Date(lecture.endTime) : null
      const { slot, duration } = formatLectureWindow(start, end)

      return {
        id: lecture._id,
        title: lecture.topic,
  cohort: lecture.module?.batch?.batchName || lecture.batch?.batchName || 'Unassigned batch',
        mode: lecture.deliveryMode || 'Live session',
        slot,
        duration,
        meetingUrl: lecture.meeting?.meetingUrl || lecture.meetingLink || null,
      }
    })
  }, [dashboard])

  const upcomingByBatch = useMemo(() => {
    if (!dashboard?.upcomingLectures?.length) return {}
    return dashboard.upcomingLectures.reduce((acc, lecture) => {
      const batchId = lecture.module?.batch?._id || lecture.batch?._id
      if (!batchId) return acc
      const existing = acc[batchId]
      if (!existing || new Date(lecture.startTime) < new Date(existing.startTime)) {
        acc[batchId] = lecture
      }
      return acc
    }, {})
  }, [dashboard])

  const cohortCards = useMemo(() => {
    const batchCollection = dashboard?.assignedBatches || dashboard?.managedBatches
    if (!batchCollection?.length) return []
    const totalStudents = dashboard.stats?.totalStudents || 0

    return batchCollection.map((batch) => {
      const nextLecture = upcomingByBatch[batch._id]
      const momentumBase = totalStudents > 0 ? Math.round((batch.studentCount / totalStudents) * 100) : 100
      const momentum = Math.min(100, Math.max(10, momentumBase || 0))
      const focus = batch.course?.title || 'Curriculum in progress'
      const nextSlot = nextLecture
        ? formatLectureWindow(new Date(nextLecture.startTime), new Date(nextLecture.endTime)).slot
        : 'No class scheduled'

      return {
        id: batch._id,
        name: batch.batchName,
        status: statusBadge(batch.status),
        focus,
        momentum,
        nextSlot,
        studentCount: batch.studentCount ?? 0,
      }
    })
  }, [dashboard, upcomingByBatch])

  const insightSignals = useMemo(() => {
    if (!dashboard?.stats) {
      return [
        {
          icon: FiActivity,
          title: 'Engagement velocity',
          value: '—',
          caption: 'Tracking student touchpoints',
          accent: 'Syncing data',
        },
        {
          icon: FiMonitor,
          title: 'Content resonance',
          value: '—',
          caption: 'Measuring sentiment',
          accent: 'Syncing feedback',
        },
        {
          icon: FiCompass,
          title: 'Career alignment',
          value: '—',
          caption: 'Matching mentors and goals',
          accent: 'Syncing outcomes',
        },
      ]
    }

    const { stats } = dashboard
    const batchCount =
      stats?.assignedBatches ?? stats?.managedBatches ?? stats?.totalBatches ?? 0
    return [
      {
        icon: FiActivity,
        title: 'Engagement velocity',
  value: `${stats?.totalStudents ?? 0} students`,
  caption: 'Active across your ecosystem',
  accent: `${batchCount} batches`,
      },
      {
        icon: FiMonitor,
        title: 'Modules in flight',
        value: `${stats?.totalModules ?? 0}`,
        caption: 'Curriculum being delivered',
        accent: 'Live right now',
      },
      {
        icon: FiCompass,
        title: 'Sessions scheduled',
        value: `${stats?.upcomingLectures ?? stats?.totalLectures ?? 0}`,
        caption: 'Remaining this week',
        accent: `${scheduleEntries.length} upcoming`,
      },
    ]
  }, [dashboard, scheduleEntries])

  const heroEyebrow = dashboard?.greeting || `${greetingMessage}, ${user?.firstName || 'mentor'}`
  const heroTitle = dashboard?.message || 'Design transformational learning moments today.'

  const handleLaunchLecture = (meetingUrl) => {
    if (!meetingUrl) return
    window.open(meetingUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="overview-shell">
      <section className="overview-hero">
        <div className="overview-hero__copy">
          <span className="hero-eyebrow">{heroEyebrow}</span>
          <h1>{heroTitle}</h1>
          <p className="hero-meta">
            {formattedDate} · {formattedTime}
          </p>
          <div className="hero-actions">
            <Link to="/lectures/new" className="hero-cta hero-cta--primary">
              Schedule lecture
            </Link>
            <Link to="/settings" className="hero-cta hero-cta--subtle">
              Personalize workspace
            </Link>
          </div>
        </div>
        <div className="overview-hero__stats">
          {highlightMetrics.map((stat) => {
            const Icon = stat.icon
            return (
              <article key={stat.label} className="hero-stat">
                <span className="hero-stat__icon">
                  <Icon size={20} />
                </span>
                <div>
                  <p>{stat.label}</p>
                  <h2>{stat.value}</h2>
                  <span>{stat.trend}</span>
                </div>
              </article>
            )
          })}
        </div>
        <div className="hero-glow" aria-hidden="true" />
      </section>

      <section className="quick-actions">
        {quickActions.map((action) => {
          const Icon = action.icon
          return (
            <Link key={action.label} to={action.to} className="quick-card" aria-label={action.label}>
              <span className="quick-card__icon">
                <Icon size={18} />
              </span>
              <div className="quick-card__body">
                <strong>{action.label}</strong>
                <p>{action.caption}</p>
              </div>
            </Link>
          )
        })}
      </section>

      <section className="overview-grid">
        <article className="panel schedule-panel">
          <header>
            <div>
              <h3>Today&apos;s flow</h3>
              <p>Key sessions orchestrated across batches</p>
            </div>
            <Link to="/lectures" className="text-link">
              See timeline
            </Link>
          </header>

          {isLoading && !dashboard ? (
            <div className="panel-state">Synchronising your schedule…</div>
          ) : scheduleEntries.length === 0 ? (
            <div className="panel-state">No upcoming sessions. Ready to schedule the next experience?</div>
          ) : (
            <ul className="schedule-list">
              {scheduleEntries.map((item) => (
                <li key={item.id} className="schedule-card">
                  <div className="schedule-card__time">
                    <strong>{item.slot}</strong>
                    <span>{item.duration}</span>
                  </div>
                  <div className="schedule-card__details">
                    <h4>{item.title}</h4>
                    <p>{item.cohort}</p>
                    <span>{item.mode}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLaunchLecture(item.meetingUrl)}
                    disabled={!item.meetingUrl}
                  >
                    {item.meetingUrl ? 'Launch room' : 'Link pending'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="panel cohort-panel">
          <header>
            <div>
              <h3>Batch pulse</h3>
              <p>Momentum signals from active programmes</p>
            </div>
            <Link to="/batches" className="text-link">
              Manage batches
            </Link>
          </header>

          {isLoading && !dashboard ? (
            <div className="panel-state">Loading your batches…</div>
          ) : cohortCards.length === 0 ? (
            <div className="panel-state">No assigned batches yet. Coordinate with the admin team to get started.</div>
          ) : (
            <div className="cohort-grid">
              {cohortCards.map((cohort) => (
                <article key={cohort.id} className="cohort-card">
                  <div className="cohort-card__header">
                    <h4>{cohort.name}</h4>
                    <span>{cohort.status}</span>
                  </div>
                  <p>{cohort.focus}</p>
                  <div className="cohort-card__progress">
                    <div className="progress-track">
                      <span style={{ width: `${cohort.momentum}%` }} />
                    </div>
                    <strong>{cohort.studentCount} students · {cohort.momentum}% momentum</strong>
                  </div>
                  <span className="next-slot">Next · {cohort.nextSlot}</span>
                  <button type="button">Open workspace</button>
                </article>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="insight-grid">
        {insightSignals.map((signal) => {
          const Icon = signal.icon
          return (
            <article key={signal.title} className="insight-card">
              <span className="insight-card__icon">
                <Icon size={20} />
              </span>
              <div className="insight-card__body">
                <h4>{signal.title}</h4>
                <strong>{signal.value}</strong>
                <p>{signal.caption}</p>
              </div>
              <span className="insight-card__accent">{signal.accent}</span>
            </article>
          )
        })}
      </section>

      {error && (
        <div className="panel-error" role="alert">
          Unable to refresh dashboard data: {error}
        </div>
      )}
    </div>
  )
}

export default Overview
